import { useCallback, useEffect, useRef, type CSSProperties } from "react";
import { ensureZeegAssets, openZeegPopup } from "@/lib/zeeg";
import { pushTrackedEvent, type TrackedEvent } from "@/lib/analytics-events";
import simonCtaPhoto from "@/assets/team/simon-mai-cta.webp.asset.json";



/** Einheitliche Beschriftung aller Knöpfe, die die Terminbuchung öffnen. */
export const BOOKING_CTA_LABEL = "Kostenloses Erstgespräch vereinbaren";
/** Einheitliche Beschriftung aller Knöpfe, die auf das Kontaktformular führen. */
export const INQUIRY_CTA_LABEL = "Unverbindlich anfragen";

export const DEFAULT_BOOKING_EVENT_PATH = "creategy/erstgespraech";

type BookingVariant = "primary" | "secondary" | "secondaryOnDark";

const VARIANT_CLASSES: Record<BookingVariant, string> = {
  primary:
    "bg-brand-button text-brand-button-foreground hover:opacity-90 focus-visible:outline-brand-button",
  secondary:
    "border border-primary bg-transparent text-primary hover:bg-primary/5 focus-visible:outline-primary",
  // Auf farbigen Sektionen (Grün/Petrol) wäre ein weißer Umriss mit weißer
  // Schrift zu kontrastarm (2,2:1 bzw. 4,4:1). Deshalb weiße Fläche mit
  // Dark-Teal-Schrift: 9,73:1, bleibt gegenüber dem Amber-Knopf nachrangig.
  secondaryOnDark:
    "border border-background bg-background text-primary hover:bg-background/90 focus-visible:outline-background",

};

interface BookingCtaButtonProps {
  eventPath?: string;
  variant?: BookingVariant;
  label?: string;
  className?: string;
  style?: CSSProperties;
  /** Kleines Foto links vom Text (Standard: an). */
  showPhoto?: boolean;
  /** Optionale Messkennzeichnung (Ereignis im bestehenden Tag Manager). */
  eventName?: TrackedEvent;
}


/**
 * Einziger Weg, die Terminbuchung zu öffnen (Popup über src/lib/zeeg.ts).
 */
export function BookingCtaButton({
  eventPath = DEFAULT_BOOKING_EVENT_PATH,
  variant = "secondary",
  label = BOOKING_CTA_LABEL,
  className = "",
  style,
  showPhoto = true,
  eventName,
}: BookingCtaButtonProps) {
  const btnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const el = btnRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            ensureZeegAssets();
            obs.disconnect();
          }
        }
      },
      { rootMargin: "800px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const onClick = useCallback(() => {
    if (eventName) pushTrackedEvent(eventName);
    void openZeegPopup(eventPath);
  }, [eventPath, eventName]);


  return (
    <button
      ref={btnRef}
      type="button"
      onClick={onClick}
      style={style}
      className={`inline-flex w-full items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-md px-5 h-11 font-display text-sm font-extrabold transition-colors sm:w-auto ${VARIANT_CLASSES[variant]} ${className}`}
    >
      {showPhoto && (
        <img
          src={simonCtaPhoto.url}
          alt=""
          aria-hidden="true"
          width={40}
          height={40}
          loading="lazy"
          decoding="async"
          className="-my-2.5 size-10 shrink-0 rounded-sm object-cover"
        />
      )}

      {label}
    </button>

  );
}
